import React, {memo} from "react";
import PropTypes from "prop-types";
import {
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import humanizeString from "string-humanize";
import ViewLoadingSpinner from "../../Containers/Cell/View/ViewLoader";
import ZoomOutMessageContainer from "../../Containers/ZoomOutMessageContainer";
import {useHandleChartData} from "../../Helpers/useHandleChartData";
import {RenderThresholdMarkers} from "./thresholdMarkers";

/** This component will generate Plot of type threshold.
 *
 * This component uses LineChart component of recharts library : https://recharts.org/en-US/api/LineChart
 */

const ThresholdPlot = (props) => {
  const {properties} = props;
  const {axes = {}, legend = {}, line_keys: lines = [], thresholds, xColumn} = properties;
  const {hide: legendHide = false} = legend;
  const xAxis = axes.x || {};
  const yAxis = axes.y || [];

  const {
    colorHexes,
    formatValue,
    isLoading,
    setXDomainLeft,
    setXDomainRight,
    showMessage,
    timeOnXAxis,
    timeOnYAxis,
    visibleData,
    xDomainLeft,
    xDomainRight,
    zoomIn,
    zoomOut,
  } = useHandleChartData(props);

  return (
    <ZoomOutMessageContainer showMessage={showMessage}>
      {isLoading ? (
        <ViewLoadingSpinner />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={visibleData}
            onDoubleClick={zoomOut}
            onMouseDown={(nextState = {}) => setXDomainLeft(nextState.activeLabel || "")}
            onMouseMove={(nextState = {}) =>
              xDomainLeft && setXDomainRight(nextState.activeLabel || "")
            }
            onMouseUp={zoomIn}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              allowDataOverflow={true}
              dataKey={xColumn}
              domain={["auto", "auto"]}
              scale={timeOnXAxis ? "time" : undefined}
              tick={{fontSize: 10, fontFamily: "Roboto, Arial"}}
              tickFormatter={(value) => formatValue(value, "x", xAxis)}
              type={timeOnXAxis ? "number" : undefined}
            />
            <YAxis
              domain={["auto", "auto"]}
              orientation={"left"}
              scale={timeOnYAxis ? "time" : undefined}
              tick={{fontSize: 10, fontFamily: "Roboto, Arial"}}
              tickFormatter={(value) => formatValue(value, "y", yAxis[0])}
              type={timeOnYAxis ? "number" : undefined}
            />
            <Tooltip
              formatter={(value) => formatValue(value, "y", yAxis[0])}
              labelFormatter={(label) => formatValue(label, "x", xAxis)}
            />
            {!legendHide && <Legend />}
            {lines.map(({key}, index) => {
              if (!key) return null;
              return (
                <Line
                  animationDuration={300}
                  dataKey={key}
                  key={key}
                  name={humanizeString(key.replaceAll(".", "_"))}
                  stroke={colorHexes[index]}
                  type="monotone"
                />
              );
            })}
            {thresholds && thresholds.length > 0 && (
              <RenderThresholdMarkers
                data={visibleData}
                thresholds={thresholds}
                valueColumns={lines.map(({key}) => key)}
              />
            )}
            {xDomainLeft && xDomainRight ? (
              <ReferenceArea
                opacity={0.2}
                x1={xDomainLeft}
                x2={xDomainRight}
                fill="#006dd9"
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ZoomOutMessageContainer>
  );
};

ThresholdPlot.propTypes = {
  jsonData: PropTypes.array.isRequired,
  properties: PropTypes.object.isRequired,
  timeZone: PropTypes.string,
};

export default memo(ThresholdPlot);
